import { HandleableError } from "@context-providers/error-handler/error-handler-types";
import { HttpUtils } from "@stellar/utils";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

/**
 * Converts errors of unknown shape to a readable markup.
 */
export function getErrorDisplayMarkup(error: HandleableError): string {
  try {
    // If error comes from one of our Stellar http clients
    if (HttpUtils.isFetchError(error)) {
      // If error comes from the Core API client return the message or error_v2 property
      if (FaroCoreApiUtils.Client.isResponseError(error.errorBody)) {
        const errorBody = error.errorBody;
        return errorBody.message || errorBody.error_v2;
      }

      // If error body is an object with a message property
      if (HttpUtils.isErrorObjectWithMessage(error.errorBody)) {
        return error.errorBody.message;
      }

      // Otherwise default to the FetchError message
      return error.message;
    }

    // If error is a generic error
    if (error instanceof Error) {
      return error.message;
    }

    // If error is an object with a message property. Example: SerializedError from Redux Toolkit
    if (HttpUtils.isErrorObjectWithMessage(error)) {
      return error.message;
    }

    // If error is a string
    if (typeof error === "string") {
      return error;
    }
  } catch (_) {
    return "No error message";
  }

  // If error is of unknown shape return a default message
  return "No error message";
}

/**
 * @returns Whether the error originates from the backend, meaning its message
 * is dynamic and may embed user data (names, emails, project names, etc.).
 * Such messages must be excluded from LocalizeJS translation to avoid leaking
 * user data. Errors thrown by the Dashboard itself (generic Error or string)
 * are static, translatable strings and return false.
 * @param error The error to classify.
 */
export function isErrorFromBackend(error: HandleableError): boolean {
  // Errors surfaced by the Core API client carry a backend response text.
  if (HttpUtils.isFetchError(error)) {
    return true;
  }

  // Real Error instances and plain strings are thrown by the Dashboard itself.
  if (error instanceof Error || typeof error === "string") {
    return false;
  }

  // Anything else (e.g. a serialized RTK error wrapping a backend response) is
  // treated as potentially containing user data.
  return HttpUtils.isErrorObjectWithMessage(error);
}
